import React, { forwardRef } from 'react';
import {
  Menu, MenuList, MenuButton,
  IconButton, Button,
  Flex,
  Text,
  useDisclosure
} from '@chakra-ui/react';
import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';

import { RiFilter2Line, RiFilter2Fill } from "react-icons/ri";
import { CloseIcon } from '@chakra-ui/icons';
import { FaRegCalendarAlt } from "react-icons/fa";
import "react-datepicker/dist/react-datepicker.css";

interface FilterDateRangeProps {
  filterStartValue: Date | null;
  setFilterStartValue: (value: Date | null) => void;
  filterEndValue: Date | null;
  setFilterEndValue: (value: Date | null) => void;
  minDate?: Date;
  maxDate?: Date;
}

/**
 * フィルター（日付範囲）
 * @param filterStartValue フィルター値
 * @param setFilterStartValue フィルター値設定関数
 * @param filterEndValue フィルター値
 * @param setFilterEndValue フィルター値設定関数
 * @returns フィルター（テキストボックス）
 */
const FilterDateRange: React.FC<FilterDateRangeProps> = ({ filterStartValue, setFilterStartValue, filterEndValue, setFilterEndValue, minDate, maxDate }) => {
  /** 翻訳 */
  const { t, i18n } = useTranslation();

  /** ロケールに応じた日付表示形式 */
  const dateFormat = i18n.language === 'en' ? 'MMM d, yyyy' : 'yyyy-MM-dd';

  /** 状態管理 */
  const { isOpen, onClose, onToggle } = useDisclosure();

  const CustomInput = forwardRef<HTMLButtonElement, { value?: string; onClick?: () => void; onClear?: () => void; }>(
    ({ value, onClick, onClear }, ref) => (
      <Flex position="relative" w="180px" border="1px" borderColor="#EEEEEE" borderRadius="0.375rem" backgroundColor={value ? "inherit" : "#eee"}>
        <Button onClick={onClick} ref={ref} leftIcon={<FaRegCalendarAlt />} w="180px" fontWeight={value ? "bold" : "normal"} backgroundColor={value ? "inherit" : "#eee"} justifyContent="flex-start">
          {value || t('common.placeholder.dateInput')}
        </Button>
        {value && (
          <IconButton
            aria-label="ClearDate"
            icon={<CloseIcon boxSize={3} />}
            onClick={onClear}
            size="sm"
            position="absolute"
            top="50%"
            right="8px"
            transform="translateY(-50%)"
          />
        )}
      </Flex>
    )
  );

  return (
    <span style={{ position: 'relative' }}>
      <Menu isOpen={isOpen} onClose={onClose} colorScheme="white" closeOnSelect={false}>
        {/* フィルターアイコン */}
        {filterStartValue || filterEndValue ? <MenuButton as={IconButton} height="16px" icon={<RiFilter2Fill />} onClick={onToggle} /> : <MenuButton as={IconButton} height="16px" icon={<RiFilter2Line />} onClick={onToggle} />}

        {isOpen && (
          <>
            <MenuList boxShadow="md">
              <Flex align="center" margin="0px 8px">
                <DatePicker
                  selected={filterStartValue}
                  onChange={(date: Date | null) => setFilterStartValue(date)}
                  dateFormat={dateFormat}
                  placeholderText="StartProject"
                  minDate={minDate}
                  maxDate={maxDate}
                  customInput={<CustomInput onClear={() => setFilterStartValue(null)} />}
                />
                <Text>{"  ～ "}</Text>
                <DatePicker
                  selected={filterEndValue}
                  onChange={(date: Date | null) => setFilterEndValue(date)}
                  dateFormat={dateFormat}
                  placeholderText="StartProject"
                  minDate={minDate}
                  maxDate={maxDate}
                  customInput={<CustomInput onClear={() => setFilterEndValue(null)} />}
                />
              </Flex>
            </MenuList>
          </>
        )}
      </Menu>
    </span>
  );
}

export default FilterDateRange;